import { message } from 'antd';
import axios, { GenericAbortSignal } from 'axios';
import md5 from 'crypto-js/md5';
import Cookies from 'js-cookie';
import qs from 'qs';

import useLoginStore from '@/store/Login';
import { removeCookie } from '@/utils';

import { isCnArea, isDev, isPre, isQA, isUsArea } from './envHelper';
import { getCookie } from './index';
import { findIntl } from './international';

export interface ResponseProps<DataProps = any> {
  success: boolean;
  code: number;
  message: string;
  requestId: string;
  data: DataProps;
  [key: string]: any;
}
export interface ErrorType {
  isError: boolean;
  message: string;
}

export const baseURLConfig = {
  us: {
    dev: 'https://medi-qa.fireflyfusion.cn',
    pre: 'https://api-us.boomcut.ai',
    online: 'https://api-usa.falcocut.com',
  },
};

export const baseURL = baseURLConfig.us[isDev || isQA ? 'dev' : isPre ? 'pre' : 'online'];

// const baseURL =
//   env === 'development' || env === 'qa'
//     ? 'https://api-chn.merrorai.cn'
//     : 'https://api-chn.merrorai.cn';
// const baseURL = 'http://api-sgp.merrorai.com'; // 新加坡

axios.interceptors.request.use(
  (config) => {
    return config;
  },
  (error) => {
    return Promise.reject(error);
  },
);
axios.interceptors.response.use(
  async (response) => {
    if (response.config?.method === 'get') {
      return response.data;
    } else {
      try {
        let validLogin = await checkLogin(response);
        if (validLogin) {
          let validErrorHandler = await errorHandler(response.data);
          if (validErrorHandler) {
            return response.data;
          }
        }
      } catch (error) {
        throw error;
      }
    }
  },
  (error) => {
    // return Promise.reject(error.response);
    return Promise.reject(error.response || error);
  },
);

function errorHandler(data: any) {
  if (data.code !== 200) {
    if (findIntl(`merror.httpError.${data.code}`)) {
      return Promise.reject({
        isError: true,
        message: findIntl(`merror.httpError.${data.code}`),
        data,
      });
    }
  }

  return Promise.resolve(true);
}

/**
 * 登录校验
 * @param {*} response
 */
function checkLogin(response: any) {
  if (
    response.data.code === 12201001 ||
    response.data.code === 12301001 ||
    response.data.code === 12301005 ||
    response.data.code === 12301006
  ) {
    removeCookie('user');
    removeCookie('userInfo');
    localStorage.clear();
    window.location.href = window.location.origin + '/home';
    const { resetLoginStore } = useLoginStore.getState();
    resetLoginStore();

    return Promise.reject({
      isError: true,
      message: findIntl(`merror.httpError.${response.data.code}`),
    });
  }
  return Promise.resolve(true);
}

/**
 * 判断输入数据类型
 * @param {any} params
 */
const myType = (params: string) => {
  let ty = 'undefined';
  ty = Object.prototype.toString.call(params);
  const res: Record<string, any> = {
    '[object Object]': 'object',
    '[object Array]': 'array',
    '[object String]': 'string',
    '[object Null]': 'null',
    '[object Number]': 'number',
    '[object Undefined]': 'undefined',
    '[object Function]': 'function',
    '[object AsyncFunction]': 'function',
    '[object AsyncGeneratorFunction]': 'function',
    '[object GeneratorFunction]': 'function',
    '[object Boolean]': 'boolean',
    '[object Date]': 'date',
    '[object Location]': 'browser',
    '[object Navigator]': 'browser',
    '[object Window]': 'browser',
    '[object History]': 'browser',
    '[object Screen]': 'browser',
  };
  if (/HTML/.test(ty)) return 'html';
  if (res[ty] !== 'number') return res[ty];
  return (
    {
      Infinity: 'infinity',
      NaN: 'nan',
    }[params.toString()] || res[ty]
  );
};

/**
 * 判断是否是空数据 {}, [], Null, '', undefind, Boolean
 * @param {*} obj any
 */
export const isEmpty = (obj: any) => {
  const type = myType(obj);
  // 约定：传入boolean类型，说明实际是有值的
  if (type === 'boolean') return false;
  if (type === 'string') return obj.length === 0;
  if (type === 'nan') return true;
  if (type === 'array') return obj.length === 0;
  if (type === 'object') return Object.keys(obj).length === 0;
  if (['date', 'html', 'browser', 'number', 'infinity', 'function'].includes(type))
    return false;
  if (!obj) return true;
  // return false;
};

/**
 * 检查 obj 中 key 值为空的数据并删除，返回 object
 * @param {Object} obj
 */
export const checkObjValue = (obj: any = {}) => {
  const param: any = {};
  if (isEmpty(obj) || myType(obj) !== 'object') return param;

  // for in  => Non-enumeration attribute
  // ckObjValue#for-of x 310, 613 ops / sec ±1.00 % (85 runs sampled)
  // ckObjValue#map x 318, 321 ops / sec ±0.50 % (87 runs sampled)
  // Fastest is ckObjValue#map
  // for-of 与 map 效率差不多 (keys arr 缓存与否都一样)

  Object.keys(obj).forEach((key) => {
    const item: any = obj[key];
    if (myType(item) === 'object') {
      // deprecated arguments.callee
      param[key] = checkObjValue(item);
    } else if (!isEmpty(item)) {
      param[key] = item;
    }
  });

  return param;
};

const urlParams = (url: string) => {
  const index = url.indexOf('?');
  if (index === -1) {
    return {};
  }

  const str = url.slice(index + 1);
  if (!str) return {};
  const param = qs.parse(str);
  // 筛除空字段
  const filterData = checkObjValue(param);
  return filterData;
};

const getRegionAndLanguage = () => {
  let regionCode = 'OTHER';
  let language = 'en_OTHER';

  let userLanguage = navigator.language;
  const [lang] = userLanguage.split('-');
  if (isCnArea) {
    // 国内
    regionCode = 'CN';
    if (['zh', 'en'].includes(lang)) {
      language = `${lang}_${regionCode}`;
    } else {
      language = 'zh_CN';
    }
  } else {
    //  海外
    language = 'en'; // `${lang}_OTHER`;
    regionCode = 'US';
  }

  // const locale = new Intl.Locale(lang).maximize();
  // locale.region && (regionCode = locale.region);
  // locale.language && (language = `${locale.language}_${regionCode}`);

  return {
    regionCode,
    language,
  };
};

// 自定义最大值
// const appKey = '10000100';
// const appKey = '100000TS';
let appKey: string;
if (process.env.NEXT_PUBLIC_ENV === 'pre') {
  appKey = '100000TS';
} else {
  appKey = '10000100';
}
/**
 * 请求
 * @param {*} url
 * @param {*} opts
 */
const request = (
  url: string,
  opts: {
    method: string;
    data?: any;
    headers?: any;
    timeout?: number;
    signal?: GenericAbortSignal;
  },
  checkDataEmptyValue: boolean = true, // 是否检查并清除空值 （有些字段需要传空值,设为false即可）
  hasBaseUrl?: boolean,
) => {
  const timestamp = Date.now();

  let headers: { [name: string]: any } = {
    'content-type': 'application/json; charset=UTF-8',
    // 鉴权
    timestamp,
    Authorization: md5(`ff39c9e3fce9f5623f121771c0ec9f88${timestamp}`),
    'client-type': 'web',
  };

  const userAuth = getCookie('user');

  if (userAuth.token) {
    // 已登录
    headers['Accesstoken'] = userAuth.token;
  }

  if (opts && opts.headers) {
    headers = Object.assign({}, headers, opts.headers);
  }

  const options = {
    ...opts,
    headers,
  };

  const method = (options?.method || 'GET').toLocaleUpperCase();

  // 声明query & body
  let query = '';
  let data = options.data || {};

  // 设置query&body
  if (method === 'GET') {
    const index = url.indexOf('?');
    let filterData = {};
    if (index > -1) {
      filterData = urlParams(url);
      url = url.slice(0, index);
    }
    query = qs.stringify(filterData);
  } else {
    let filterData = checkDataEmptyValue ? checkObjValue(data) : data;
    options.data = {
      productId: 20017,
      appKey,
      ...getRegionAndLanguage(),
      content: filterData,
    };
    if (userAuth.uid) {
      options.data.user = userAuth.uid;
      options.data.userId = userAuth.uid;
    }
    if (userAuth.tenantId) options.data.tenantId = userAuth.tenantId;
  }

  url = `${url}?${query}`;

  return axios({
    ...options,
    baseURL: hasBaseUrl ? url : baseURL,
    url,
    method,
  })
    .then((data) => {
      return data;
    })
    .catch((error) => {
      return error;
    });
};

export default request;
